import type { Viewer } from '../../../viewer/Viewer';
import * as THREE from 'three/webgpu';
import { AssemblyComponentFixationService } from '../../component-fixation';
import { recomputeAssemblyDatumReferences } from '../../../reference/shared/recomputeAssemblyDatumReferences';
import {
  applyComponentInstanceSnapshotToObject,
  createAssemblyTransformHistoryRecord,
  createComponentInstanceSnapshotFromObject,
  type AssemblyTransformHistoryRecord,
  type ComponentInstanceSnapshot,
} from '../../component-instance';
import { MateSolveStatus } from '../../definition';
import { resolveMoveComponentTransformTarget } from '../MoveComponentTransformTargetResolver';
import { AssemblyTransformToolMode } from '../MoveComponentTypes';
import { createAssemblyComponentDrivenConstraintsForToolMode } from '../AssemblyComponentTransformIntent';
import { resolveAssemblyComponentTransformLinkContext } from '../AssemblyComponentTransformLinkResolver';
import { AssemblyConstrainedRotationProjector } from './AssemblyConstrainedRotationProjector';
import type { AssemblyMateComponentRotationAxis } from '../../mate';

//------ 线性阵列同源旋转锚点 开始------
/**
 * 目标对象旋转预览的局部变换快照。
 */
interface RotatePreviewTransformSnapshot {
  /** 初始局部位置。 */
  position: THREE.Vector3;
  /** 初始局部旋转。 */
  quaternion: THREE.Quaternion;
  /** 初始局部缩放。 */
  scale: THREE.Vector3;
  /** 捕获时的世界矩阵。 */
  worldMatrix: THREE.Matrix4;
}
//------ 线性阵列同源旋转锚点 结束------

/**
 * 目标对象旋转预览快照。
 */
interface RotatePreviewSnapshot {
  /** 当前预览绑定对象。 */
  target: THREE.Object3D;
  //------ 线性阵列同源旋转锚点 开始------
  /** 当前预览需要同步旋转的对象组。 */
  linkedObjects: THREE.Object3D[];
  /** 线性阵列自由旋转时用于解析共同中心的源对象；普通零部件为空。 */
  rotationAnchorObject: THREE.Object3D | null;
  /** Mate 刚性组合自由旋转时用于计算共同 bbox 中心的对象集合。 */
  rotationPivotObjects: THREE.Object3D[];
  /** 联动对象初始组件实例快照。 */
  linkedComponentSnapshots: Map<string, ComponentInstanceSnapshot>;
  /** 联动对象初始局部变换快照。 */
  linkedTransformSnapshots: Map<string, RotatePreviewTransformSnapshot>;
  /**
   * 整个连通装配的对象集合，用于“剩余自由度不支持鼠标方向时整体绕鼠标方向刚性旋转”。
   *
   * 说明：仅当选中零件所在的已提交配合连通装配全浮动（可整体自由旋转）时非空；含固定件时为空，
   * 让上层回退到剩余轴约束旋转。整组做刚体运动会保持全部内部配合。
   */
  connectedGroupObjects: THREE.Object3D[];
  /** 连通装配对象初始组件实例快照，用于每帧回到基线与提交/回滚。 */
  connectedGroupComponentSnapshots: Map<string, ComponentInstanceSnapshot>;
  /** 连通装配对象初始局部变换快照，用于整体刚性旋转的基线。 */
  connectedGroupTransformSnapshots: Map<string, RotatePreviewTransformSnapshot>;
  //------ 线性阵列同源旋转锚点 结束------
  /**
   * 上一帧是否采用了“整个连通装配整体刚性旋转”。
   *
   * 说明：整体刚性旋转直接移动整组、求解为空转；约束旋转只移动 linkedObjects、其余由求解联动。两种模式的
   * 提交历史来源不同，需按最后一帧实际采用的模式记录，避免漏记整组成员或与求解联动记录重复。
   */
  appliedConnectedGroupRotation: boolean;
  /** CAD 历史使用的完整组件实例 before 快照。 */
  initialComponentSnapshot: ComponentInstanceSnapshot;
}

/**
 * 旋转服务。
 *
 * 设计说明：
 * 1. 仅负责旋转求解能力边界；
 * 2. 第一阶段提供可调用空实现，保证控制链路完整；
 * 3. 具体几何更新将在后续迭代由统一事务链接入。
 */
export class RotateService {
  private readonly viewer: Viewer;
  private previewSnapshot: RotatePreviewSnapshot | null = null;
  /** Mate 实时求解涉及组件的旋转初始快照。 */
  private readonly mateSolveInitialSnapshots = new Map<string, ComponentInstanceSnapshot>();
  /** Mate 实时求解涉及组件的最新快照。 */
  private readonly mateSolveLatestSnapshots = new Map<string, ComponentInstanceSnapshot>();
  /** 旋转增量欧拉角复用缓存，避免预览高频创建对象。 */
  private readonly deltaEuler = new THREE.Euler();
  /** 旋转增量四元数复用缓存，避免预览高频创建对象。 */
  private readonly deltaQuaternion = new THREE.Quaternion();
  /** 已提交 Mate 剩余转轴投影器，确保不同旋转入口使用同一套自由度语义。 */
  private readonly constrainedRotationProjector = new AssemblyConstrainedRotationProjector();
  /** 世界空间旋转 delta。 */
  private readonly constrainedWorldDeltaMatrix = new THREE.Matrix4();
  /** 组件旋转后 world 矩阵缓存。 */
  private readonly nextTargetWorldMatrix = new THREE.Matrix4();
  /** 父级 world 矩阵逆矩阵缓存。 */
  private readonly parentWorldInverseMatrix = new THREE.Matrix4();
  /** 组件旋转后 local 矩阵缓存。 */
  private readonly nextTargetLocalMatrix = new THREE.Matrix4();
  //------ 线性阵列同源旋转锚点 开始------
  /** 自由旋转的世界空间 delta 矩阵。 */
  private readonly freeRotationWorldDeltaMatrix = new THREE.Matrix4();
  /** 共同旋转中心到世界空间的平移矩阵。 */
  private readonly pivotToWorldMatrix = new THREE.Matrix4();
  /** 世界空间到共同旋转中心的平移矩阵。 */
  private readonly worldToPivotMatrix = new THREE.Matrix4();
  /** 解析旋转中心包围盒时复用的缓存。 */
  private readonly rotationPivotBounds = new THREE.Box3();
  /** 旋转中心世界坐标缓存。 */
  private readonly rotationPivotWorld = new THREE.Vector3();
  //------ 线性阵列同源旋转锚点 结束------
  /** 鼠标旋转轴（世界）复用缓存，用于与剩余转轴比较方向。 */
  private readonly mouseRotationAxis = new THREE.Vector3();
  /** 剩余转轴（世界）复用缓存。 */
  private readonly residualRotationAxis = new THREE.Vector3();
  /**
   * 判定“剩余自由度支持鼠标方向”的方向夹角余弦阈值。
   *
   * 鼠标旋转轴与剩余转轴夹角在 ~45° 内视为一致，沿剩余轴约束旋转；否则往上整组绕鼠标方向刚性旋转。
   */
  private static readonly RESIDUAL_AXIS_ALIGNMENT_COS = Math.cos(Math.PI / 4);
  /** 鼠标旋转输入可忽略的最小弧度模长。 */
  private static readonly MIN_ROTATION_INPUT = 1e-9;

  /**
   * 创建旋转服务。
   *
   * @param viewer 场景视图实例。
   */
  public constructor(viewer: Viewer) {
    this.viewer = viewer;
  }

  /**
   * 预览 Delta XYZ 旋转。
   *
   * @param deltaRotation 旋转增量。
   */
  public previewRotateByDelta(deltaRotation: readonly [number, number, number]): void {
    const snapshot = this.ensurePreviewSnapshot();
    if (!snapshot) {
      return;
    }

    this.deltaEuler.set(
      THREE.MathUtils.degToRad(deltaRotation[0]),
      THREE.MathUtils.degToRad(deltaRotation[1]),
      THREE.MathUtils.degToRad(deltaRotation[2]),
      'XYZ',
    );
    this.deltaQuaternion.setFromEuler(this.deltaEuler);

    // 每次预览都从同一个 before 基线重算，避免闭环求解在上一帧结果上继续累计。
    this.restorePreviewBaseline(snapshot);
    this.applyRotationPreviewWithGroupEscalation(snapshot, deltaRotation);
    this.solveCommittedMatesForPreview(snapshot);
    this.recomputeDependentAssemblyDatumReferences();
    this.requestRenderAfterTransform();
  }

  /**
   * 按“零件剩余轴 → 整个连通装配整体 → 回退剩余轴”的顺序应用旋转预览。
   *
   * 设计说明（对齐 SOLIDWORKS 的“整体转动”体感）：
   * 1. 剩余自由度支持鼠标方向（鼠标旋转轴与剩余转轴夹角在阈值内）→ 沿剩余轴约束旋转，驱动闭环随动；
   * 2. 不支持鼠标方向 → 往上把整个已提交配合连通装配当作单一自由刚体，绕鼠标方向整体刚性旋转。整组刚体
   *    运动保持全部内部配合，剩余自由度的轴也随之转动，因此不会破坏零件之间的配合；
   * 3. 连通装配含固定件无法整体自由旋转时 → 回退到把鼠标 delta 投影到剩余轴的原有约束旋转；
   * 4. 既无剩余轴也无可整体旋转的连通装配（真正的孤立零件）→ 自由旋转。
   *
   * @param snapshot 当前旋转预览快照。
   * @param deltaRotation 用户输入的 XYZ 旋转增量，单位为度。
   */
  private applyRotationPreviewWithGroupEscalation(
    snapshot: RotatePreviewSnapshot,
    deltaRotation: readonly [number, number, number],
  ): void {
    const rotationAxis = this.viewer.assemblyMateController?.resolveComponentRotationAxisForTransform?.({
      componentInstanceId: snapshot.initialComponentSnapshot.componentInstanceId,
    }) ?? null;
    snapshot.appliedConnectedGroupRotation = false;

    // 1. 剩余自由度支持鼠标方向 → 沿剩余轴约束旋转。
    if (
      rotationAxis
      && this.isMouseDirectionAlignedWithResidualAxis(deltaRotation, rotationAxis)
      && this.applyConstrainedAxisRotationPreview(snapshot, deltaRotation, rotationAxis)
    ) {
      return;
    }

    // 2. 不支持鼠标方向 → 整个连通装配绕鼠标方向整体刚性旋转（保配合、剩余轴一起转）。
    if (this.applyConnectedGroupRigidRotationPreview(snapshot)) {
      snapshot.appliedConnectedGroupRotation = true;
      return;
    }

    // 3. 无法整体旋转 → 回退到把鼠标 delta 投影到剩余轴。
    if (rotationAxis && this.applyConstrainedAxisRotationPreview(snapshot, deltaRotation, rotationAxis)) {
      return;
    }

    // 4. 孤立零件 → 自由旋转。
    this.applyFreeRotationPreview(snapshot);
  }

  /**
   * 判断鼠标旋转方向是否与零件剩余转轴一致（即剩余自由度支持该方向的旋转）。
   *
   * @param deltaRotation 用户输入的 XYZ 旋转增量，单位为度。
   * @param rotationAxis Mate 图解析出的剩余转轴。
   * @returns 鼠标旋转轴与剩余转轴夹角在阈值内时返回 true。
   */
  private isMouseDirectionAlignedWithResidualAxis(
    deltaRotation: readonly [number, number, number],
    rotationAxis: { readonly direction: readonly [number, number, number] },
  ): boolean {
    this.mouseRotationAxis.set(
      THREE.MathUtils.degToRad(deltaRotation[0]),
      THREE.MathUtils.degToRad(deltaRotation[1]),
      THREE.MathUtils.degToRad(deltaRotation[2]),
    );
    if (this.mouseRotationAxis.lengthSq() <= RotateService.MIN_ROTATION_INPUT ** 2) {
      // 无有效鼠标旋转输入时，保持原有约束旋转语义（视为“沿剩余轴”）。
      return true;
    }
    this.residualRotationAxis.fromArray(rotationAxis.direction);
    if (this.residualRotationAxis.lengthSq() <= RotateService.MIN_ROTATION_INPUT ** 2) {
      return false;
    }
    this.mouseRotationAxis.normalize();
    this.residualRotationAxis.normalize();
    return Math.abs(this.mouseRotationAxis.dot(this.residualRotationAxis))
      >= RotateService.RESIDUAL_AXIS_ALIGNMENT_COS;
  }

  /**
   * 把整个连通装配当作单一自由刚体，绕鼠标方向整体刚性旋转。
   *
   * @param snapshot 当前旋转预览快照。
   * @returns 存在可整体旋转的连通装配并已应用时返回 true。
   */
  private applyConnectedGroupRigidRotationPreview(snapshot: RotatePreviewSnapshot): boolean {
    if (snapshot.connectedGroupObjects.length === 0) {
      return false;
    }

    const pivotWorld = this.resolveRotationPivotObjectsCenterWorld(snapshot.connectedGroupObjects)
      ?? this.resolveRotationPivotWorld(snapshot.connectedGroupObjects[0]!);
    this.freeRotationWorldDeltaMatrix.makeRotationFromQuaternion(this.deltaQuaternion);
    this.applyRigidWorldDeltaToPreviewObjects(
      snapshot.connectedGroupObjects,
      snapshot.connectedGroupTransformSnapshots,
      this.freeRotationWorldDeltaMatrix,
      pivotWorld,
    );
    return true;
  }

  /**
   * 提交旋转预览。
   */
  public commitPreview(): AssemblyTransformHistoryRecord | null {
    // 提交时保留当前姿态，仅清空会话快照。
    if (!this.previewSnapshot) {
      return null;
    }
    const snapshot = this.previewSnapshot;

    if (!this.solveCommittedMatesForPreview(snapshot, { updateDocumentStatus: true })) {
      return null;
    }

    const linkedRecords = this.createLinkedTransformHistoryRecords('rotate', snapshot);
    const primaryRecord = linkedRecords.find((record) => {
      return record.before.componentInstanceId === snapshot.initialComponentSnapshot.componentInstanceId;
    }) ?? linkedRecords[0] ?? null;
    const relatedRecords = [
      ...linkedRecords.filter((record) => record !== primaryRecord),
      ...this.createMateSolveRelatedRecords('rotate', snapshot),
    ];

    this.previewSnapshot = null;
    this.clearMateSolveSnapshots();

    if (!primaryRecord) {
      return relatedRecords[0] && relatedRecords.length > 1
        ? {
            ...relatedRecords[0],
            relatedRecords: relatedRecords.slice(1),
          }
        : relatedRecords[0] ?? null;
    }

    return relatedRecords.length > 0
      ? {
          ...primaryRecord,
          relatedRecords,
        }
      : primaryRecord;
  }

  /**
   * 回滚旋转预览。
   */
  public cancelPreview(): void {
    if (!this.previewSnapshot) {
      return;
    }

    this.restoreLinkedComponentSnapshots(this.previewSnapshot);
    this.restoreObjectsToComponentSnapshots(
      this.previewSnapshot.connectedGroupObjects,
      this.previewSnapshot.connectedGroupComponentSnapshots,
    );
    this.rollbackMateSolvedPreviewObjects();
    this.previewSnapshot.target.updateMatrixWorld(true);
    this.recomputeDependentAssemblyDatumReferences();
    this.updateChangedInstanceMatrices();
    this.previewSnapshot = null;
    this.clearMateSolveSnapshots();
    this.viewer.requestRender();
  }

  /**
   * 清理旋转服务资源。
   */
  public dispose(): void {
    this.cancelPreview();
  }

  /**
   * 确保当前存在有效旋转快照。
   *
   * @returns 可用快照；无目标对象时返回 null。
   */
  private ensurePreviewSnapshot(): RotatePreviewSnapshot | null {
    const selectedTarget = resolveMoveComponentTransformTarget(
      this.viewer.getSelectedObjects()[0] ?? null,
      this.viewer,
    );
    if (!selectedTarget) {
      return null;
    }
    const transformLinkContext = resolveAssemblyComponentTransformLinkContext(this.viewer, selectedTarget);
    const canTransformAllLinkedObjects = transformLinkContext.linkedObjects.every((object) => {
      return AssemblyComponentFixationService.canTransformSceneObject(object);
    });
    if (!canTransformAllLinkedObjects) {
      // SolidWorks 中只有明确浮动的零部件才能被“旋转零部件”命令改变姿态；这里在会话入口收口。
      if (this.previewSnapshot?.target === selectedTarget) {
        this.previewSnapshot = null;
      }
      return null;
    }

    if (this.previewSnapshot?.target === selectedTarget) {
      return this.previewSnapshot;
    }

    const initialComponentSnapshot = createComponentInstanceSnapshotFromObject({
      object: selectedTarget,
    });
    const connectedGroupObjects = this.resolveConnectedGroupTransformObjects(
      initialComponentSnapshot.componentInstanceId,
    );
    this.previewSnapshot = {
      target: selectedTarget,
      //------ 线性阵列同源旋转锚点 开始------
      linkedObjects: transformLinkContext.linkedObjects,
      rotationAnchorObject: transformLinkContext.rotationAnchorObject,
      rotationPivotObjects: transformLinkContext.rotationPivotObjects,
      linkedComponentSnapshots: this.captureLinkedComponentSnapshots(transformLinkContext.linkedObjects),
      linkedTransformSnapshots: this.captureLinkedTransformSnapshots(transformLinkContext.linkedObjects),
      //------ 线性阵列同源旋转锚点 结束------
      connectedGroupObjects,
      connectedGroupComponentSnapshots: this.captureLinkedComponentSnapshots(connectedGroupObjects),
      connectedGroupTransformSnapshots: this.captureLinkedTransformSnapshots(connectedGroupObjects),
      appliedConnectedGroupRotation: false,
      initialComponentSnapshot,
    };
    this.clearMateSolveSnapshots();
    return this.previewSnapshot;
  }

  /**
   * 解析选中零件所在的整个连通装配（可整体自由旋转时）的联动对象。
   *
   * @param componentInstanceId 选中零件组件实例 ID。
   * @returns 可整体刚性旋转的连通装配对象；不可整体旋转时为空数组。
   */
  private resolveConnectedGroupTransformObjects(componentInstanceId: string): THREE.Object3D[] {
    const objects = this.viewer.assemblyMateController
      ?.resolveFloatingConnectedLinkedTransformObjectsForTransform?.({ componentInstanceId }) ?? [];
    return [...objects];
  }

  //------ 线性阵列同源旋转锚点 开始------
  /**
   * 捕获联动对象的组件实例快照。
   *
   * @param linkedObjects 本次旋转联动对象。
   * @returns 对象 ID 到组件实例快照的映射。
   */
  private captureLinkedComponentSnapshots(
    linkedObjects: readonly THREE.Object3D[],
  ): Map<string, ComponentInstanceSnapshot> {
    const snapshots = new Map<string, ComponentInstanceSnapshot>();
    linkedObjects.forEach((object) => {
      snapshots.set(object.uuid, createComponentInstanceSnapshotFromObject({
        object,
      }));
    });
    return snapshots;
  }

  /**
   * 捕获联动对象的局部变换和世界矩阵快照。
   *
   * @param linkedObjects 本次旋转联动对象。
   * @returns 对象 ID 到局部变换快照的映射。
   */
  private captureLinkedTransformSnapshots(
    linkedObjects: readonly THREE.Object3D[],
  ): Map<string, RotatePreviewTransformSnapshot> {
    const snapshots = new Map<string, RotatePreviewTransformSnapshot>();
    linkedObjects.forEach((object) => {
      object.updateMatrixWorld(true);
      snapshots.set(object.uuid, {
        position: object.position.clone(),
        quaternion: object.quaternion.clone(),
        scale: object.scale.clone(),
        worldMatrix: object.matrixWorld.clone(),
      });
    });
    return snapshots;
  }

  /**
   * 恢复联动对象的组件实例快照。
   *
   * @param snapshot 当前旋转预览快照。
   */
  private restoreLinkedComponentSnapshots(snapshot: RotatePreviewSnapshot): void {
    snapshot.linkedObjects.forEach((object) => {
      const componentSnapshot = snapshot.linkedComponentSnapshots.get(object.uuid);
      if (!componentSnapshot) {
        return;
      }

      applyComponentInstanceSnapshotToObject(object, componentSnapshot);
      object.updateMatrixWorld(true);
    });
  }

  /**
   * 创建本次数值旋转产生的联动历史记录。
   *
   * @param operation 操作类型。
   * @param snapshot 当前旋转预览快照。
   * @returns 发生变化的组件历史记录集合。
   */
  private createLinkedTransformHistoryRecords(
    operation: 'rotate',
    snapshot: RotatePreviewSnapshot,
  ): AssemblyTransformHistoryRecord[] {
    // 整体刚性旋转直接移动整个连通装配，需按整组成员及其基线记录；否则只记录 linkedObjects，其余由求解联动记录承担。
    const [objects, beforeSnapshots] = snapshot.appliedConnectedGroupRotation
      ? [snapshot.connectedGroupObjects, snapshot.connectedGroupComponentSnapshots] as const
      : [snapshot.linkedObjects, snapshot.linkedComponentSnapshots] as const;

    return objects
      .map((object) => {
        const beforeSnapshot = beforeSnapshots.get(object.uuid);
        if (!beforeSnapshot) {
          return null;
        }

        const afterSnapshot = createComponentInstanceSnapshotFromObject({
          object,
          assemblyId: beforeSnapshot.assemblyId,
          componentInstanceId: beforeSnapshot.componentInstanceId,
        });

        return createAssemblyTransformHistoryRecord(operation, beforeSnapshot, afterSnapshot);
      })
      .filter((record): record is AssemblyTransformHistoryRecord => Boolean(record));
  }

  /**
   * 判断组件 ID 是否已由当前联动旋转组负责记录。
   *
   * @param snapshot 当前旋转预览快照。
   * @param componentInstanceId 组件实例 ID。
   * @returns 已属于联动组时返回 `true`。
   */
  private isLinkedPreviewComponent(
    snapshot: RotatePreviewSnapshot,
    componentInstanceId: string,
  ): boolean {
    return [...snapshot.linkedComponentSnapshots.values()].some((componentSnapshot) => {
      return componentSnapshot.componentInstanceId === componentInstanceId;
    });
  }

  /**
   * 解析对象的世界包围盒中心作为旋转中心。
   *
   * @param object 用于提供旋转中心的对象。
   * @returns 写入复用缓存后的世界旋转中心。
   */
  private resolveRotationPivotWorld(object: THREE.Object3D): THREE.Vector3 {
    this.rotationPivotBounds.makeEmpty();
    this.expandRotationBoundsByBusinessObject(object);
    if (!this.rotationPivotBounds.isEmpty()) {
      this.rotationPivotBounds.getCenter(this.rotationPivotWorld);
      return this.rotationPivotWorld;
    }

    object.getWorldPosition(this.rotationPivotWorld);
    return this.rotationPivotWorld;
  }

  /**
   * 解析 Mate 刚性组合的世界 bbox 中心。
   *
   * @param objects 需要共同计算 bbox 的对象集合。
   * @returns 刚性组合存在有效可见包围盒时返回中心，否则返回 `null`。
   */
  private resolveRotationPivotObjectsCenterWorld(objects: readonly THREE.Object3D[]): THREE.Vector3 | null {
    if (objects.length <= 1) {
      return null;
    }

    this.rotationPivotBounds.makeEmpty();
    objects.forEach((object) => {
      this.expandRotationBoundsByBusinessObject(object);
    });
    if (this.rotationPivotBounds.isEmpty()) {
      return null;
    }

    this.rotationPivotBounds.getCenter(this.rotationPivotWorld);
    return this.rotationPivotWorld;
  }

  /**
   * 把业务对象或其已脱离场景树的实例 source Mesh 加入旋转中心包围盒。
   *
   * @param object occurrence 根、装配祖先或普通场景对象。
   */
  private expandRotationBoundsByBusinessObject(object: THREE.Object3D): void {
    const sourceMeshes = this.viewer.listImportedModelInstancedSourceMeshes?.(object) ?? [];
    if (sourceMeshes.length === 0) {
      object.updateMatrixWorld(true);
      this.rotationPivotBounds.expandByObject(object);
      return;
    }

    sourceMeshes.forEach((sourceMesh) => {
      this.viewer.prepareImportedModelSourceMeshForSpatialRead?.(sourceMesh);
      this.rotationPivotBounds.expandByObject(sourceMesh);
    });
  }
  //------ 线性阵列同源旋转锚点 结束------

  /**
   * 将当前预览对象和上一轮 Mate 联动对象恢复到本次操作的 before 基线。
   *
   * 设计说明：
   * 旋转面板里的 delta 值表示本轮预览相对初始姿态的目标量，而不是在每一帧继续累加。
   * 因此在应用新的 delta 前必须先恢复目标组件和已联动组件，避免闭环求解在上一帧结果上继续漂移。
   *
   * @param snapshot 当前旋转预览快照。
   */
  private restorePreviewBaseline(snapshot: RotatePreviewSnapshot): void {
    this.restoreLinkedComponentSnapshots(snapshot);
    // 整组刚性旋转会移动连通装配里 linkedObjects 之外的成员，回到基线时必须一并恢复。
    this.restoreObjectsToComponentSnapshots(
      snapshot.connectedGroupObjects,
      snapshot.connectedGroupComponentSnapshots,
    );
    this.rollbackMateSolvedPreviewObjects();
    this.mateSolveLatestSnapshots.clear();
  }

  /**
   * 把给定对象组恢复到其初始组件实例快照。
   *
   * @param objects 需要恢复的对象组。
   * @param componentSnapshots 对象 uuid 到组件实例快照的映射。
   */
  private restoreObjectsToComponentSnapshots(
    objects: readonly THREE.Object3D[],
    componentSnapshots: Map<string, ComponentInstanceSnapshot>,
  ): void {
    objects.forEach((object) => {
      const componentSnapshot = componentSnapshots.get(object.uuid);
      if (!componentSnapshot) {
        return;
      }
      applyComponentInstanceSnapshotToObject(object, componentSnapshot);
      object.updateMatrixWorld(true);
    });
  }

  /**
   * 在已提交 Mate 保留的旋转自由度内应用旋转预览。
   *
   * @param snapshot 当前旋转预览快照。
   * @param deltaRotation 用户输入的 XYZ 旋转增量，单位为度。
   * @returns 是否已按配合自由度应用旋转。
   */
  private applyConstrainedAxisRotationPreview(
    snapshot: RotatePreviewSnapshot,
    deltaRotation: readonly [number, number, number],
    resolvedRotationAxis?: AssemblyMateComponentRotationAxis | null,
  ): boolean {
    const rotationAxis = resolvedRotationAxis
      ?? this.viewer.assemblyMateController?.resolveComponentRotationAxisForTransform?.({
        componentInstanceId: snapshot.initialComponentSnapshot.componentInstanceId,
      })
      ?? null;
    if (!rotationAxis) {
      return false;
    }

    const signedAngle = this.constrainedRotationProjector.projectEulerDeltaToAxisAngle(
      deltaRotation,
      rotationAxis,
    );
    if (!this.constrainedRotationProjector.buildAxisRotationDeltaMatrix(
      rotationAxis,
      signedAngle,
      this.constrainedWorldDeltaMatrix,
    )) {
      return false;
    }

    this.applyWorldDeltaToLinkedPreviewObjects(snapshot, this.constrainedWorldDeltaMatrix, false);
    return true;
  }

  //------ 线性阵列同源旋转锚点 开始------
  /**
   * 应用无 Mate 轴约束的自由旋转预览。
   *
   * @param snapshot 当前旋转预览快照。
   */
  private applyFreeRotationPreview(snapshot: RotatePreviewSnapshot): void {
    const hasSharedPivot = Boolean(snapshot.rotationAnchorObject)
      || snapshot.rotationPivotObjects.length > 0;
    if (!hasSharedPivot) {
      // 普通零部件继续沿用原有语义：只改变对象自身姿态，不引入额外平移。
      snapshot.linkedObjects.forEach((object) => {
        const baselineTransform = snapshot.linkedTransformSnapshots.get(object.uuid);
        if (!baselineTransform) {
          return;
        }

        object.position.copy(baselineTransform.position);
        object.quaternion.copy(baselineTransform.quaternion).multiply(this.deltaQuaternion);
        object.scale.copy(baselineTransform.scale);
        object.updateMatrixWorld(true);
      });
      return;
    }

    this.freeRotationWorldDeltaMatrix.makeRotationFromQuaternion(this.deltaQuaternion);
    this.applyWorldDeltaToLinkedPreviewObjects(snapshot, this.freeRotationWorldDeltaMatrix, true);
  }

  /**
   * 将世界空间 delta 应用到联动预览对象组。
   *
   * @param snapshot 当前旋转预览快照。
   * @param worldDelta 世界空间刚体旋转 delta。
   * @param useRotationAnchor 是否围绕线性阵列源对象锚点旋转。
   */
  private applyWorldDeltaToLinkedPreviewObjects(
    snapshot: RotatePreviewSnapshot,
    worldDelta: THREE.Matrix4,
    useRotationAnchor: boolean,
  ): void {
    const sharedPivotWorld = useRotationAnchor
      ? (
          snapshot.rotationAnchorObject
            ? this.resolveRotationPivotWorld(snapshot.rotationAnchorObject)
            : this.resolveRotationPivotObjectsCenterWorld(snapshot.rotationPivotObjects)
        )
      : null;
    this.applyRigidWorldDeltaToPreviewObjects(
      snapshot.linkedObjects,
      snapshot.linkedTransformSnapshots,
      worldDelta,
      sharedPivotWorld,
    );
  }
  //------ 线性阵列同源旋转锚点 结束------

  /**
   * 将世界空间刚体 delta（可选绕共同枢轴）应用到给定对象组的基线位姿。
   *
   * 说明：约束旋转、线性阵列自由旋转、整个连通装配整体旋转共用同一套“基线世界矩阵 → 应用 delta → 写回 local”
   * 的刚体写入逻辑，避免不同入口对枢轴与父级坐标转换理解不一致。
   *
   * @param objects 需要刚性旋转的对象组。
   * @param transformSnapshots 对象基线局部变换与世界矩阵快照。
   * @param worldDelta 世界空间刚体旋转 delta。
   * @param pivotWorld 共同旋转枢轴世界坐标；为 null 时 delta 自身已含枢轴，不再额外平移。
   */
  private applyRigidWorldDeltaToPreviewObjects(
    objects: readonly THREE.Object3D[],
    transformSnapshots: Map<string, RotatePreviewTransformSnapshot>,
    worldDelta: THREE.Matrix4,
    pivotWorld: THREE.Vector3 | null,
  ): void {
    if (pivotWorld) {
      this.pivotToWorldMatrix.makeTranslation(pivotWorld.x, pivotWorld.y, pivotWorld.z);
      this.worldToPivotMatrix.makeTranslation(-pivotWorld.x, -pivotWorld.y, -pivotWorld.z);
    }

    objects.forEach((object) => {
      const baselineTransform = transformSnapshots.get(object.uuid);
      if (!baselineTransform) {
        return;
      }

      if (pivotWorld) {
        this.nextTargetWorldMatrix
          .copy(this.pivotToWorldMatrix)
          .multiply(worldDelta)
          .multiply(this.worldToPivotMatrix)
          .multiply(baselineTransform.worldMatrix);
      } else {
        this.nextTargetWorldMatrix.copy(worldDelta).multiply(baselineTransform.worldMatrix);
      }

      if (object.parent) {
        object.parent.updateWorldMatrix(true, false);
        this.parentWorldInverseMatrix.copy(object.parent.matrixWorld).invert();
        this.nextTargetLocalMatrix.copy(this.parentWorldInverseMatrix).multiply(this.nextTargetWorldMatrix);
      } else {
        this.nextTargetLocalMatrix.copy(this.nextTargetWorldMatrix);
      }

      this.nextTargetLocalMatrix.decompose(
        object.position,
        object.quaternion,
        object.scale,
      );
      object.updateMatrix();
      object.updateWorldMatrix(true, true);
    });
  }

  private solveCommittedMatesForPreview(
    snapshot: RotatePreviewSnapshot,
    options: { readonly updateDocumentStatus?: boolean } = {},
  ): boolean {
    const solveResult = this.viewer.assemblyMateController?.solveCommittedMatesAfterComponentTransform({
      anchorComponentInstanceId: snapshot.initialComponentSnapshot.componentInstanceId,
      drivenComponentConstraints: createAssemblyComponentDrivenConstraintsForToolMode(
        snapshot.initialComponentSnapshot.componentInstanceId,
        AssemblyTransformToolMode.Rotate,
      ),
      /**
       * 旋转预览属于临时姿态求解，不能在 pointermove 级路径中写配合文档诊断状态。
       * 提交入口会显式开启诊断刷新，保证最终状态仍能持久化。
       */
      updateDocumentStatus: options.updateDocumentStatus ?? false,
    }) ?? null;
    if (!solveResult) {
      return true;
    }
    if (solveResult.status !== MateSolveStatus.Solved) {
      this.restoreLinkedComponentSnapshots(snapshot);
      this.rollbackMateSolvedPreviewObjects();
      this.mateSolveLatestSnapshots.clear();
      return false;
    }

    solveResult.beforeSnapshots.forEach((beforeSnapshot) => {
      if (this.isLinkedPreviewComponent(snapshot, beforeSnapshot.componentInstanceId)) {
        return;
      }
      if (!this.mateSolveInitialSnapshots.has(beforeSnapshot.componentInstanceId)) {
        this.mateSolveInitialSnapshots.set(beforeSnapshot.componentInstanceId, beforeSnapshot);
      }
    });
    solveResult.afterSnapshots.forEach((afterSnapshot) => {
      if (this.isLinkedPreviewComponent(snapshot, afterSnapshot.componentInstanceId)) {
        return;
      }
      this.mateSolveLatestSnapshots.set(afterSnapshot.componentInstanceId, afterSnapshot);
    });
    return true;
  }

  /**
   * 创建 Mate 实时求解产生的联动历史记录。
   *
   * @param operation 操作类型。
   * @param snapshot 当前旋转预览快照。
   * @returns 联动历史记录。
   */
  private createMateSolveRelatedRecords(
    operation: 'rotate',
    snapshot: RotatePreviewSnapshot,
  ): AssemblyTransformHistoryRecord[] {
    return [...this.mateSolveInitialSnapshots.entries()]
      .map(([componentInstanceId, beforeSnapshot]) => {
        if (this.isLinkedPreviewComponent(snapshot, componentInstanceId)) {
          return null;
        }

        const afterSnapshot = this.mateSolveLatestSnapshots.get(componentInstanceId);
        if (!afterSnapshot) {
          return null;
        }

        return createAssemblyTransformHistoryRecord(operation, beforeSnapshot, afterSnapshot);
      })
      .filter((record): record is AssemblyTransformHistoryRecord => Boolean(record));
  }

  /**
   * 回滚 Mate 实时求解移动过的组件。
   */
  private rollbackMateSolvedPreviewObjects(): void {
    this.mateSolveInitialSnapshots.forEach((snapshot) => {
      const object = this.viewer.assemblyMateController?.resolveSceneObjectForComponentSnapshot(snapshot) ?? null;
      if (!object) {
        return;
      }
      applyComponentInstanceSnapshotToObject(object, snapshot);
    });
  }

  /**
   * 清理 Mate 实时求解快照。
   */
  private clearMateSolveSnapshots(): void {
    this.mateSolveInitialSnapshots.clear();
    this.mateSolveLatestSnapshots.clear();
  }

  //------ 装配体基准引用移动后重算 开始------
  /**
   * 重算依赖当前装配体位姿的基准引用。
   *
   * 设计说明：
   * 1. 旋转零部件会改变特征点、线、面的世界方向和位置；
   * 2. 由这些拓扑创建的基准面、基准轴、基准点不能只在提交后更新，预览和回滚也必须立即复算；
   * 3. 服务层只触发装配体位姿变化通知，具体引用解析仍保持在各基准控制器中。
   */
  private recomputeDependentAssemblyDatumReferences(): void {
    recomputeAssemblyDatumReferences(this.viewer);
  }

  /** 直接更新旋转预览实际影响的实例矩阵槽，再请求普通场景渲染。 */
  private requestRenderAfterTransform(): void {
    this.updateChangedInstanceMatrices();
    // 所有联动实例槽先同步，帧循环只绘制本帧最终姿态，避免预览重复提交管线。
    this.viewer.requestRender();
  }

  /**
   * 收集旋转主目标、阵列/连通装配联动对象与 Mate 求解对象，并直接同步对应实例矩阵。
   */
  private updateChangedInstanceMatrices(): void {
    const changedObjects = new Set<THREE.Object3D>();
    const snapshot = this.previewSnapshot;
    if (snapshot) {
      changedObjects.add(snapshot.target);
      snapshot.linkedObjects.forEach(object => changedObjects.add(object));
      snapshot.connectedGroupObjects.forEach(object => changedObjects.add(object));
    }
    const mateSnapshots = [
      ...this.mateSolveInitialSnapshots.values(),
      ...this.mateSolveLatestSnapshots.values(),
    ];
    mateSnapshots.forEach((componentSnapshot) => {
      const object = this.viewer.assemblyMateController
        ?.resolveSceneObjectForComponentSnapshot?.(componentSnapshot) as THREE.Object3D | null | undefined;
      if (object) {
        changedObjects.add(object);
      }
    });
    this.viewer.updateImportedModelInstanceMatrices?.([...changedObjects]);
  }
  //------ 装配体基准引用移动后重算 结束------
}
